import { TimeData } from "@/components/molecules/TimeSelect";
import {
  BookingSchedule,
  BookingScheduleDate,
  BookingScheduleDay,
  BookingScheduleTime,
} from "@/lib/types/schedule-types";

export const defaultScheduleDay: BookingScheduleDay = {
  start: {
    date: null,
    time: null,
  },
  end: {
    date: null,
    time: null,
  },
};

export const parseBookingSchedule = (
  schedule: BookingSchedule,
): BookingSchedule => {
  if (!schedule) {
    return [];
  }

  return schedule.map((day) => {
    const startTimeObj = TimeData.find(
      (timeObj) =>
        timeObj.h === day.start.time?.h && timeObj.m === day.start.time.m,
    );
    const start = {
      ...day.start,
      time: startTimeObj as BookingScheduleTime,
    };

    const endTimeObj = TimeData.find((timeObj) => {
      const isNextDay =
        day.start.date?.d &&
        day.end.date?.d &&
        (day.end.date.d > day.start.date.d ||
          day.end.date.m > day.start.date.m);
      if (isNextDay) {
        return (
          timeObj.overnight &&
          timeObj.h === day.end.time?.h &&
          timeObj.m === day.end.time?.m
        );
      }
      return timeObj.h === day.end.time?.h && timeObj.m === day.end.time.m;
    });
    const end = {
      ...day.end,
      time: endTimeObj as BookingScheduleTime,
    };

    return { start, end };
  });
};

export const dateToBookingSchedule = (
  date: Date,
): BookingScheduleDate | null => {
  if (!date) {
    return null;
  }

  return {
    y: date?.getFullYear(),
    m: date?.getMonth() + 1,
    d: date?.getDate(),
  };
};

export const bookingScheduleToDate = (
  bookingScheduleDate: BookingScheduleDate,
): Date | null => {
  if (!bookingScheduleDate) {
    return null;
  }

  return new Date(
    bookingScheduleDate?.y,
    bookingScheduleDate?.m - 1,
    bookingScheduleDate?.d,
  );
};

export const validateScheduleDate = (
  date: BookingScheduleDate | null,
  allowPastDates = false,
): boolean => {
  if (!date?.y || !date?.m || !date?.d) {
    return false;
  }

  const scheduleDate = new Date(date.y, date.m - 1, date.d);

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day (00:00:00)

  if (!allowPastDates && scheduleDate < today) {
    return false;
  }

  return true;
};

export const validateScheduleTime = (
  time: BookingScheduleTime | null,
): boolean => {
  if ((!time?.h && time?.h !== 0) || (!time?.m && time?.m !== 0)) {
    return false;
  }

  return true;
};

export const validateScheduleDay = (
  day: BookingScheduleDay,
  allowPastDates = false,
): boolean => {
  if (!day) {
    return false;
  }

  const { start, end } = day || {};

  if (
    !validateScheduleDate(start.date, allowPastDates) ||
    !validateScheduleTime(start.time) ||
    !validateScheduleDate(end.date, allowPastDates) ||
    !validateScheduleTime(end.time)
  ) {
    return false;
  }

  const startDate = new Date(
    start.date!.y,
    start.date!.m - 1,
    start.date!.d,
    start.time!.h,
    start.time!.m,
  );

  const endDate = new Date(
    end.date!.y,
    end.date!.m - 1,
    end.date!.d,
    end.time!.h,
    end.time!.m,
  );

  if (startDate >= endDate) {
    return false;
  }

  return true;
};
